import { memo, RefObject, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';

import AgGridCellRenderer from '@/components/common/TableGrid/CustomComponents/AgGridCellRenderer/AgGridCellRenderer';
import AgGridDatepickerTag4Editor
  from '@/components/common/TableGrid/CustomComponents/AgGridDatepicker/AgGridDatepickerTag4Editor';
import AgGridDropdownEditor from '@/components/common/TableGrid/CustomComponents/AgGridDropdown/AgGridDropdownEditor';
import AgGridSelectableFilter
  from '@/components/common/TableGrid/CustomComponents/AgGridSelectableFilter/AgGridSelectableFilter';
import { UpliftData_contractLine } from '../hooks/useConvertUpliftDataFormat';
import ApplyChanges from './ApplyChanges';
import { TableGrid } from '@/components/common/TableGrid/TableGrid';
import useUpdateBulkContractLineUplift from '../hooks/useUpdateBulkContractLinesUplift';
import { BILLING_TYPES } from '@/config/statuses.config';
import { UpliftMethodEnum } from '@/graphql/contracts/_generated_/contracts';

interface IUpliftContractLinesGridProps {
  data: (UpliftData_contractLine | null)[];
  columns: ColDef[];
  promptReference: RefObject<HTMLDivElement>;
  clearUplifts: (action: UpliftAction) => void;
}

export enum Action {
  APPLY,
  CANCEL
}

export type UpliftAction = Action;

export function UpliftContractLinesGrid({
  data,
  columns,
  promptReference,
  clearUplifts
}: IUpliftContractLinesGridProps) {
  const gridRef = useRef<AgGridReact<UpliftData_contractLine>>(null);
  const { updateBulkContractLines } = useUpdateBulkContractLineUplift();

  const marginIfPromptVisible = promptReference?.current?.className === 'inline-apply-visible' ? '97px' : undefined;

  const getAffectedContractLines = (): UpliftData_contractLine[] => {
    if (!gridRef.current) return [];
    const rows: UpliftData_contractLine[] = [];

    gridRef.current.api.forEachNode((node) => {
      if (node?.data?.upliftValue || node?.data?.upliftValue === 0) {
        rows.push(node.data);
      }
    });
    return rows;
  };

  const getUpliftMethodToBeClearedOff = (): UpliftData_contractLine[] => {
    if (!gridRef.current) return [];
    const rowsWithUpliftMethod: UpliftData_contractLine[] = [];

    gridRef.current.api.forEachNode((node) => {

      if ((node.data as UpliftData_contractLine).billingType) {
        rowsWithUpliftMethod.push((node.data as UpliftData_contractLine));
      }
    });
    return rowsWithUpliftMethod;
  };

  const clearGrid = (rows: UpliftData_contractLine[], action: UpliftAction) => {
    if (!gridRef.current || !rows?.length) return;

    rows.forEach((row) => {
      const id = row.contractLineID;
      const contractLine = gridRef.current!.api.getRowNode(id)?.data;

      if (!contractLine) return;

      if (action === Action.CANCEL) {
        contractLine.action = Action.CANCEL;
        clearUplifts(Action.CANCEL);
      }

      if (action === Action.APPLY) {
        contractLine.action = Action.APPLY;
        clearUplifts(Action.APPLY);
      }
    });

    promptReference.current!.className = 'inline-apply-hidden';
    gridRef.current.api.refreshCells();
  };

  const cancelApply = () => {
    if (!gridRef.current) return;
    const rows = getAffectedContractLines();
    const rowsWithUpliftMethodOnly = getUpliftMethodToBeClearedOff();
    return rows.length ? clearGrid(rows, Action.CANCEL) : clearGrid(rowsWithUpliftMethodOnly, Action.CANCEL);
  };

  const applyUplift = async () => {
    if (!gridRef.current) return;
    const rows = getAffectedContractLines();

    const rowsWithUpliftMethodOnly = getUpliftMethodToBeClearedOff();
    if (rows.length) {
      const rowsUpliftData = rows.map(row => ({
        contractLineId: row.contractLineID,
        upliftAmount: row.upliftValue,
        upliftMethod: row.billingType === BILLING_TYPES[0].formattedType ?
          UpliftMethodEnum.UNIT_COST : UpliftMethodEnum.PERCENTAGE,
        nextUpliftDate: row.nextUpliftDate
      }));
      const updatedResult = await updateBulkContractLines(rowsUpliftData);
      return updatedResult ? clearGrid(rows, Action.APPLY) : clearGrid(rows, Action.CANCEL); 
    }
    return clearGrid(rowsWithUpliftMethodOnly, Action.APPLY);
  };

  return (
    <div
      id="contract-lines-grid"
      style={{ width: '100%' }}
      className="ag-theme-alpine old"
    >
      <div style={{ marginBottom: marginIfPromptVisible }}>
        <TableGrid<UpliftData_contractLine>
          ref={gridRef}
          getRowId={(el) => el.data.contractLineID}
          suppressRowClickSelection={true}
          rowData={data as UpliftData_contractLine[]}
          columnDefs={columns}
          rowSelection="multiple"
          suppressMenuHide={true}
          components={{
            DropdownEditor: AgGridDropdownEditor,
            DatepickerEditor: AgGridDatepickerTag4Editor,
            CellRenderer: AgGridCellRenderer,
            SelectableFilter: AgGridSelectableFilter,
          }}
          defaultColDef={{ resizable: true, sortable: false }}
        />
      </div>

      <ApplyChanges onApply={applyUplift} onCancel={cancelApply} ref={promptReference} />

    </div>
  );
}

export default memo<IUpliftContractLinesGridProps>(UpliftContractLinesGrid, (prevProps, newProps) => {
  return prevProps.data === newProps.data;
});
